import { addDays, format, isValid } from 'date-fns';
import { createDateSafe, todayIsoBrt } from './dates';
import { buildHolidayBlockMessage, findBlockingHoliday } from './vacationHolidayRules';
import {
    BLOCKED_WEEKDAY_MESSAGE,
    buildScheduleLimitMessage,
    isAfterScheduleLimit,
    isBlockedStartWeekday,
} from './vacationStartDateRules';
import { isFractionedModality } from './vacationFractioning';
import type {
    VacationBalance,
    VacationModality,
    VacationSegment,
} from './vacation.types';

export const ENTITLED_DAYS_PER_PERIOD = 30;

export const SELL_DAYS = 10;

export const MIN_LONGEST_SEGMENT_DAYS = 14;

export const MIN_SEGMENT_DAYS = 5;


export interface ValidationIssue {
    code: string;
    message: string;
    field?: string;
}

export const HARD_ERROR_CODES: readonly string[] = [
    'insufficient-balance',
    'not-eligible',
    'no-balance',
];

export interface ValidationResult {
    valid: boolean;
    errors: ValidationIssue[];
    warnings: ValidationIssue[];
}

export interface ValidateStartDateOptions {
    holidays?: string[];
}

export interface ValidateRequestInput {
    modality: VacationModality;
    segments: VacationSegment[];
    sellDaysRequested: boolean;
}

export type VacationBalanceLike = Pick<VacationBalance, 'eligible' | 'balanceDays' | 'expiredDays' | 'availableDays'>;

export function hasPartialBalance(balance?: VacationBalanceLike | null): boolean {
    return Boolean(balance && balance.availableDays > 0 && balance.availableDays < ENTITLED_DAYS_PER_PERIOD);
}

export function canSubmitRequest(balance: VacationBalanceLike | null, scheduleLimitDate?: string | null): ValidationResult {
    const errors: ValidationIssue[] = [];
    const warnings: ValidationIssue[] = [];

    if (!balance) {
        warnings.push({
            code: 'balance-unavailable',
            message:
                'Seu saldo ainda não foi calculado, então não conseguimos conferir se há dias suficientes. A solicitação segue para aprovação e o DP fará a checagem.',
        });
    } else if (!balance.eligible) {
        errors.push({
            code: 'not-eligible',
            message: 'Você não tem direito a férias por este módulo.',
        });
    } else if (scheduleLimitDate && isAfterScheduleLimit(todayIsoBrt(), scheduleLimitDate)) {
        errors.push({
            code: 'schedule-limit-expired',
            message: buildScheduleLimitMessage(scheduleLimitDate),
        });
    } else if (balance.balanceDays <= 0) {
        errors.push({
            code: 'no-balance',
            message: 'Você não tem dias de férias disponíveis no momento.',
        });
    }

    return { valid: errors.length === 0, errors, warnings };
}

function toIsoString(date: Date): string {
    return format(date, 'yyyy-MM-dd');
}

export function resolvePeriodDays(balanceDays: number | null | undefined): number {
    if (!balanceDays || balanceDays <= 0) return 0;
    return balanceDays >= ENTITLED_DAYS_PER_PERIOD ? ENTITLED_DAYS_PER_PERIOD : balanceDays;
}

export function computeEnjoyedDays(
    sellDaysRequested: boolean,
    entitledDays: number = ENTITLED_DAYS_PER_PERIOD
): number {
    return entitledDays - (sellDaysRequested ? SELL_DAYS : 0);
}

export function computeEndDate(startDate: string, days: number): string {
    const start = createDateSafe(startDate);
    if (!start || days <= 0) return '';
    return toIsoString(addDays(start, days - 1));
}

export function computeReturnDate(startDate: string, days: number): string {
    const start = createDateSafe(startDate);
    if (!start || days <= 0) return '';
    return toIsoString(addDays(start, days));
}

export function validateStartDate(
    startDate: string,
    options: ValidateStartDateOptions = {}
): ValidationResult {
    const errors: ValidationIssue[] = [];
    const date = createDateSafe(startDate);

    if (!date || !isValid(date)) {
        return {
            valid: false,
            errors: [{ code: 'invalid-date', message: 'Data de início inválida' }],
            warnings: [],
        };
    }

    if (isBlockedStartWeekday(startDate)) {
        errors.push({
            code: 'weekday-blocked',
            message: BLOCKED_WEEKDAY_MESSAGE,
        });
    }

    const blockedHoliday = findBlockingHoliday(startDate, options.holidays ?? []);
    if (blockedHoliday) {
        errors.push({
            code: 'holiday-blocked',
            message: buildHolidayBlockMessage(startDate, blockedHoliday),
        });
    }

    return { valid: errors.length === 0, errors, warnings: [] };
}

function validateSegmentSplit(segments: VacationSegment[]): ValidationIssue[] {
    if (segments.length <= 1) return [];

    const issues: ValidationIssue[] = [];
    const longest = Math.max(...segments.map((segment) => segment.days));
    const shortest = Math.min(...segments.map((segment) => segment.days));

    if (longest < MIN_LONGEST_SEGMENT_DAYS) {
        issues.push({
            code: 'split-longest-too-short',
            message: `Um dos períodos precisa ter ao menos ${MIN_LONGEST_SEGMENT_DAYS} dias (art. 134, §1º da CLT). Nesta combinação de modalidade e venda de dias isso não é possível.`,
        });
    }

    if (shortest < MIN_SEGMENT_DAYS) {
        issues.push({
            code: 'split-segment-too-short',
            message: `Nenhum período pode ter menos de ${MIN_SEGMENT_DAYS} dias (art. 134, §1º da CLT).`,
        });
    }

    return issues;
}

function validateNoOverlap(segments: VacationSegment[]): ValidationIssue[] {
    const ordered = [...segments].sort((a, b) => a.startDate.localeCompare(b.startDate));

    for (let i = 1; i < ordered.length; i += 1) {
        if (ordered[i].startDate <= ordered[i - 1].endDate) {
            return [
                {
                    code: 'segments-overlap',
                    message: 'Os períodos solicitados se sobrepõem.',
                    field: 'secondStartDate',
                },
            ];
        }
    }

    return [];
}

export function validateRequest(
    input: ValidateRequestInput,
    balance: VacationBalanceLike | null,
    options: ValidateStartDateOptions = {}
): ValidationResult {
    const errors: ValidationIssue[] = [];
    const warnings: ValidationIssue[] = [];

    const segments = input.segments;
    const totalDays = segments.reduce((sum, segment) => sum + segment.days, 0);
    const requiredDays = totalDays + (input.sellDaysRequested ? SELL_DAYS : 0);

    if (isFractionedModality(input.modality) && segments.length < 2) {
        errors.push({
            code: 'missing-segment',
            message: 'Informe as datas do 2º período.',
            field: 'secondStartDate',
        });
    }

    if (!balance) {
        warnings.push({
            code: 'balance-unavailable',
            message:
                'Não foi possível conferir seu saldo agora. As demais regras foram validadas, mas o envio só é liberado com o saldo disponível.',
        });
    } else {
        if (!balance.eligible) {
            errors.push({
                code: 'not-eligible',
                message: 'Este colaborador não tem direito a férias por este módulo.',
            });
        }

        if (requiredDays > balance.availableDays) {
            errors.push({
                code: 'insufficient-balance',
                message: `Saldo insuficiente: a solicitação consome ${requiredDays} dias e o saldo disponível é de ${balance.availableDays} dias.`,
            });
        }
    }

    segments.forEach((segment, index) => {
        const field = index === 0 ? 'firstStartDate' : 'secondStartDate';

        if (!segment.startDate) {
            errors.push({
                code: 'missing-start-date',
                message: 'Informe a data de início do período.',
                field,
            });
            return;
        }

        validateStartDate(segment.startDate, options).errors.forEach((issue) =>
            errors.push({ ...issue, field })
        );
    });

    if (input.sellDaysRequested && input.modality !== '30') {
        errors.push({
            code: 'sell-requires-single-period',
            message: `Com abono pecuniário as férias são de ${computeEnjoyedDays(
                true
            )} dias em um único período — não é possível fracionar.`,
            field: 'modality',
        });
    }

    if (hasPartialBalance(balance)) {
        if (input.sellDaysRequested) {
            errors.push({
                code: 'sell-not-allowed-partial-balance',
                message: 'O abono pecuniário não é permitido quando o saldo é parcial.',
                field: 'sellDaysRequested',
            });
        }

        if (isFractionedModality(input.modality)) {
            errors.push({
                code: 'fractioned-not-allowed-partial-balance',
                message: `Quando o saldo é parcial, as férias devem ser em um único período de ${balance!.balanceDays} dias.`,
                field: 'modality',
            });
        }
    }

    errors.push(...validateSegmentSplit(segments));

    if (segments.every((segment) => segment.startDate && segment.endDate)) {
        errors.push(...validateNoOverlap(segments));
    }

    return { valid: errors.length === 0, errors, warnings };
}
